import {
  isMultileg,
  MarketAccountTypeEnum,
  useMarketAccountsContext,
  useMarketsContext,
  useSecuritiesContext,
  useSecurity,
  useUnifiedLiquidity,
  type Allocation,
  type Market,
  type MarketAccount,
  type Security,
  type UnifiedLiquidityResponse,
} from '@talos/kyoko';
import { compact, uniq } from 'lodash-es';
import { useMemo } from 'react';
import { usePermittedMarketAccountsSet } from '../../../hooks/usePermittedMarketAccountsSet';
import { useRestrictedTradingHiddenMarkets } from '../../../hooks/useRestrictedTradingHiddenMarkets';
import { isValidUnifiedLiquidityTokenSecurity } from '../utils/unifiedLiquidity';

export type UseMarketDataMarketsAndAccountsParams = { symbol: string | undefined } & Pick<
  GetMarketDataMarketsAndAccountsParams,
  'combineAllMarketAccounts' | 'includeUnifiedLiquidityMarkets' | 'subAccountAllocations'
>;

/** Hook wrapper version of `getMarketDataMarketsAndAccounts` */
export const useMarketDataMarketsAndAccounts = ({
  symbol,
  combineAllMarketAccounts,
  includeUnifiedLiquidityMarkets,
  subAccountAllocations,
}: UseMarketDataMarketsAndAccountsParams) => {
  const { marketAccountsByMarket } = useMarketAccountsContext();
  const { marketsByName } = useMarketsContext();
  const { securitiesBySymbol } = useSecuritiesContext();
  const security = useSecurity(symbol);
  const ulResponse = useUnifiedLiquidity(includeUnifiedLiquidityMarkets ? symbol : undefined);
  const hiddenMarkets = useRestrictedTradingHiddenMarkets();
  const permittedMarketAccountNames = usePermittedMarketAccountsSet({
    subAccountAllocations: subAccountAllocations ?? -1,
    combineAllMarketAccounts,
  });

  return useMemo(
    () =>
      getMarketDataMarketsAndAccounts({
        security,
        includeUnifiedLiquidityMarkets,
        ulResponse,
        marketAccountsByMarket,
        marketsByName,
        hiddenMarkets,
        securitiesBySymbol,
        permittedMarketAccountNames,
      }),
    [
      security,
      includeUnifiedLiquidityMarkets,
      ulResponse,
      marketAccountsByMarket,
      marketsByName,
      hiddenMarkets,
      securitiesBySymbol,
      permittedMarketAccountNames,
    ]
  );
};

export interface GetMarketDataMarketsAndAccountsParams {
  /**
   * The Security.Markets array will be used internally.
   * Security is required but we allow it to be undefined for ease-of-use. If undefined, the hook returns empty arrays.
   */
  security: Security | undefined;
  /** Whether or not Unified Liquidity should be used (when applicable) to collect Markets and Accounts. */
  includeUnifiedLiquidityMarkets?: boolean;
  /** The UL details for the security (if applicable) */
  ulResponse?: UnifiedLiquidityResponse;
  marketAccountsByMarket: Map<string, MarketAccount[]>;
  marketsByName: Map<string, Market>;
  /** Market names to hide when they would otherwise be returned as market-level selector options. */
  hiddenMarkets?: string[];
  securitiesBySymbol?: Map<string, Security>;
  subAccountAllocations?: Allocation[] | -1;
  combineAllMarketAccounts?: boolean;
  /**
   * When provided and not null, market accounts are filtered to only those in this set (restricted trading mode).
   * When filtering, markets are returned as [] since accounts replace their parent markets.
   */
  permittedMarketAccountNames?: Set<string> | null;
}

/**
 * The purpose of this function is to collect the markets and accounts which are eligible to be viewed in contexts such as
 * the Market Data card settings.
 *
 * The function grabs the resolved Security.Markets and flattens into Market Accounts where possible. It removes any Markets which
 * also have found eligible Market Accounts (effectively replacing the Market with its Market Accounts).
 *
 * The function does not handle multileg symbols. The Multileg case is handled separately.
 */
export function getMarketDataMarketsAndAccounts({
  security,
  includeUnifiedLiquidityMarkets,
  ulResponse,
  marketAccountsByMarket,
  marketsByName,
  hiddenMarkets,
  securitiesBySymbol,
  permittedMarketAccountNames,
}: GetMarketDataMarketsAndAccountsParams): { markets: Market[]; marketAccounts: MarketAccount[] } {
  if (!security || isMultileg(security)) {
    return { markets: [], marketAccounts: [] };
  }

  const securityMarkets = security.Markets ?? [];
  const maybeULMarkets = includeUnifiedLiquidityMarkets
    ? (ulResponse?.Tokens.filter(token => {
        if (!securitiesBySymbol) {
          return true;
        }

        return isValidUnifiedLiquidityTokenSecurity(securitiesBySymbol.get(token.Symbol));
      }).map(token => token.Market) ?? [])
    : [];
  const allMarkets = compact(uniq([...securityMarkets, ...maybeULMarkets]).map(m => marketsByName.get(m)));
  const hiddenMarketNames = new Set(hiddenMarkets ?? []);

  const marketsWithoutAnyAccounts: Market[] = [];
  const marketAccounts: MarketAccount[] = [];

  for (const market of allMarkets) {
    const marketAccountsForMarket = marketAccountsByMarket
      .get(market.Name)
      ?.filter(marketAccount => marketAccount.Type === MarketAccountTypeEnum.Trading)
      .filter(
        marketAccount => permittedMarketAccountNames == null || permittedMarketAccountNames.has(marketAccount.Name)
      );

    if (marketAccountsForMarket && marketAccountsForMarket.length > 0) {
      marketAccounts.push(...marketAccountsForMarket);
    } else if (permittedMarketAccountNames == null && !hiddenMarketNames.has(market.Name)) {
      marketsWithoutAnyAccounts.push(market);
    }
  }

  return { markets: marketsWithoutAnyAccounts, marketAccounts };
}
